// PieceRules component - Mobile-optimized piece rules viewer with animations
import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './PieceRules.module.css';
import globalStyles from '../../App.module.css';
import Board from '../Board';
import { GameContext } from '../Providers/GameContext';
import { useTheme } from '../Providers/ThemeContext';
import { useLanguage } from '../Providers/LanguageContext';
import { AuthContext } from '../Providers/AuthContext';
import GlobalHeader from '../GlobalHeader';
import WispyStars from '../Tutorials/WispyStars';
import ZodiacEngineService from '../ZodiacEngineService';
import { PieceType, Mode } from '../../services/zodiacEngine/types';
import TurnPopover from '../TurnPopover/TurnPopover';
import MoveContext from '../MoveContext/MoveContext';
import { PieceRulesAnimator } from './PieceRulesAnimator';
import { generateRandomBearMovementAnimation, getPieceAnimationDatabase } from './AnimationScenarios';
import * as AnimationScenarios from './AnimationScenarios';
import { playUISound } from '../../utils/UISoundEffects';

function PieceRules() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const { t } = useLanguage();
  const { user } = useContext(AuthContext);
  const {
    gameState,
    potentialMoves,
    potentialSideEffects,
    updateGameState,
    updateSelectedPiece,
    updatePotentialMoves,
    updatePotentialSideEffects
  } = useContext(GameContext);

  // Get initial piece from URL parameter, default to Bear if not provided
  const initialPiece = searchParams.get('piece') as PieceType || PieceType.Bear;
  const [selectedPiece, setSelectedPiece] = useState<PieceType>(initialPiece);
  const [isGameStarted, setIsGameStarted] = useState(false);
  const [selectedAbility, setSelectedAbility] = useState<string | undefined>(undefined);
  const [animator, setAnimator] = useState<PieceRulesAnimator | null>(null);
  const [currentScenarioDescription, setCurrentScenarioDescription] = useState<string | null>(null);
  const [currentPostMoveDescription, setCurrentPostMoveDescription] = useState<string | null>(null);
  const [playingScenario, setPlayingScenario] = useState<string | null>(null);

  // Mobile-specific state
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileAnimationView, setIsMobileAnimationView] = useState(false);

  // All available pieces for the dropdown
  const allPieces = [
    PieceType.Bear, PieceType.Crane, PieceType.Frog, PieceType.Fungi,
    PieceType.Goat, PieceType.Monkey, PieceType.Octopus, PieceType.Penguin,
    PieceType.Rhino, PieceType.Snake, PieceType.Tree, PieceType.Virus, PieceType.Wolf
  ];

  // Detect mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize animator and set Rules mode
  useEffect(() => {

    // Set game mode to Rules
    const currentGameState = ZodiacEngineService.zodiacEngine.getGameState();
    currentGameState.mode = Mode.Rules;

    // Update the React game state
    updateGameState(currentGameState);

    // Initialize the animator
    const newAnimator = new PieceRulesAnimator(
      ZodiacEngineService.zodiacEngine,
      updateGameState,
      Mode.Rules,
      updateSelectedPiece,
      updatePotentialMoves,
      updatePotentialSideEffects,  // Pass side effects callback for purple highlights
      undefined,  // Don't pass move description callback - we handle it manually now
      () => {
        // This callback will be called right after the movement step completes
        const currentPostMove = newAnimator.getPostMoveDescription();
        if (currentPostMove) {
          setCurrentPostMoveDescription(currentPostMove);
        }
      }
    );

    // Set up animation complete callback separately to avoid closure issues
    (newAnimator as any).setOnCompleteCallback(() => {
      // Check window width directly to avoid stale closure
      if (window.innerWidth <= 768) {
        setTimeout(() => {
          setIsMobileAnimationView(false);
        }, 1500); // Wait 1.5s after completion before auto-returning
      }
    });

    setAnimator(newAnimator);

    return () => {
      // Cleanup: stop any running animations when component unmounts
      if (newAnimator) {
        newAnimator.stopAnimation();
      }
    };
  }, [updateGameState]);

  // Update scenario descriptions when language changes
  useEffect(() => {
    // If we have a selected ability, reload its description in the new language
    if (selectedAbility && selectedPiece) {
      const database = getPieceAnimationDatabase();
      const pieceAnimations = database[selectedPiece as keyof typeof database];
      if (pieceAnimations) {
        let animation = pieceAnimations[selectedAbility as keyof typeof pieceAnimations];
        if (!animation) {
          const animationKey = selectedAbility.toLowerCase();
          animation = pieceAnimations[animationKey as keyof typeof pieceAnimations];
        }
        if (animation) {
          setCurrentScenarioDescription(animation.description);
          // Update post-move description if animator has it set
          if (animator && animation.moveDescription) {
            animator.setPostMoveDescription(animation.moveDescription);
          }
        }
      }
    }
  }, [t, selectedAbility, selectedPiece, animator]);

  const handleBack = () => {
    navigate('/');
  };

  const handleHome = () => {
    navigate('/');
  };

  const handleMobileBack = () => {
    // Stop any running animation
    if (animator) {
      animator.stopAnimation();
    }
    setIsMobileAnimationView(false);
  };

  const handlePieceSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedPiece(event.target.value as PieceType);
    setSelectedAbility(undefined); // Reset ability when piece changes
    setCurrentScenarioDescription(null); // Clear scenario description when piece changes
    setCurrentPostMoveDescription(null); // Clear post-move description when piece changes
    setPlayingScenario(null); // Clear playing scenario when piece changes
  };

  const handleAbilitySelect = async (ability: string) => {
    setSelectedAbility(ability);

    // Switch to mobile animation view if on mobile
    if (isMobile) {
      setIsMobileAnimationView(true);
    }

    if (!animator) {
      return;
    }

    // FORCE STOP: Always stop any currently running animation and wait for cleanup
    animator.stopAnimation();

    // Wait for the stop to fully complete and clear any pending animations
    // Also check if animation is still running and wait until it's fully stopped
    let retries = 0;
    const maxRetries = 10;
    while (animator.isCurrentlyAnimating() && retries < maxRetries) {
      await new Promise(resolve => setTimeout(resolve, 100));
      retries++;
    }

    if (retries >= maxRetries) {
    }

    // Additional safety delay
    await new Promise(resolve => setTimeout(resolve, 100));

    // Set active state AFTER stopping previous animation to prevent race conditions
    setPlayingScenario(ability);

    // Special handling for Bear basic movement to randomize FEN
    if (selectedPiece === 'Bear' && ability.toLowerCase() === 'movement') {
      const randomAnimation = generateRandomBearMovementAnimation();
      setCurrentScenarioDescription(randomAnimation.description);
      setCurrentPostMoveDescription(null); // Clear post-move initially
      // Set post-move description in animator BEFORE starting animation
      animator.setPostMoveDescription(randomAnimation.moveDescription || null);
      try {
        await animator.playSequence(randomAnimation);
        setIsGameStarted(true);
      } catch (error) {
      } finally {
        // Only clear active state if this is still the current scenario
        setPlayingScenario(current => current === ability ? null : current);
      }
      return;
    }

    // Get the animation sequence from the database
    const database = getPieceAnimationDatabase();
    const pieceAnimations = database[selectedPiece as keyof typeof database];
    if (!pieceAnimations) {
      return;
    }

    // Try exact key first, then lowercase for backwards compatibility
    let animation = pieceAnimations[ability as keyof typeof pieceAnimations];
    if (!animation) {
      const animationKey = ability.toLowerCase();
      animation = pieceAnimations[animationKey as keyof typeof pieceAnimations];
    }

    if (!animation) {
      return;
    }

    try {
      setCurrentScenarioDescription(animation.description);
      setCurrentPostMoveDescription(null); // Clear post-move initially
      // Set post-move description in animator BEFORE starting animation
      animator.setPostMoveDescription(animation.moveDescription || null);
      await animator.playSequence(animation);
      setIsGameStarted(true);
    } catch (error) {
    } finally {
      // Only clear active state if this is still the current scenario
      setPlayingScenario(current => current === ability ? null : current);
    }
  };

  const handleShowMovements = () => {
    // Show piece movements for the selected piece
    
    // Set up a demo board with the selected piece
    const playerIdForLight = user?.uid || 'piece-rules-player';
    
    ZodiacEngineService.zodiacEngine.switchToAIMode(playerIdForLight, selectedPiece, selectedPiece);
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
    
    setIsGameStarted(true);
  };

  const handleNewExample = () => {
    // Create a new example with the selected piece
    
    // Initialize a new game with the selected piece
    ZodiacEngineService.zodiacEngine.resetAndStartNewGame();
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
    
    setIsGameStarted(true);
  };

  const handleResetGame = () => {
    setIsGameStarted(false);
    
    // Reset to a fresh game state
    ZodiacEngineService.zodiacEngine.resetAndStartNewGame();
    const newGameState = ZodiacEngineService.zodiacEngine.getGameState();
    updateGameState(newGameState);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>, position: any) => {
    // Block user input during animations
    if (animator?.isCurrentlyAnimating()) {
      return;
    }
    
    // TODO: Implement drop logic for piece rules demonstrations
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    // Block user input during animations
    if (animator?.isCurrentlyAnimating()) {
      return;
    }
    
    event.preventDefault();
  };

  // Mobile animation view - full-screen board with back button
  if (isMobile && isMobileAnimationView) {
    return (
      <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app} ${styles.mobileAnimationPage}`} data-board-size="compact">
        <WispyStars />
        <GlobalHeader />
        <TurnPopover
          isThisPlayersTurn={gameState.isLightTurn === true}
          state={gameState.state}
          mode={gameState.mode}
        />

        {/* Mobile back button - minimal style */}
        <button
          className={styles.mobileBackButton}
          onClick={() => { playUISound(); handleMobileBack(); }}
          aria-label={t('pieceRules.backToSelection')}
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>

        <div className={styles.mobileAnimationContainer}>
          <div className={styles.boardContainer}>
            <Board
              gameState={gameState}
              uiState={{
                potentialMoves: potentialMoves,
                potentialSideEffects: potentialSideEffects,
              }}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            />
          </div>

          {/* Info boxes below the board - always show both to prevent shifting */}
          <div className={styles.mobileInfoContainer}>
            {/* Scenario description - always visible */}
            <div className={styles.mobileDescriptionBox}>
              <p>{currentScenarioDescription || '\u00A0'}</p>
            </div>

            {/* Post-move description - always visible */}
            <div className={styles.mobilePostMoveBox}>
              <p>{currentPostMoveDescription || '\u00A0'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile selection view or desktop view
  return (
    <div className={`${styles.container} ${theme === 'dark' ? styles.dark : ''} ${globalStyles.app}`}>
      <WispyStars />
      <GlobalHeader />
      <TurnPopover
        isThisPlayersTurn={gameState.isLightTurn === true}
        state={gameState.state}
        mode={gameState.mode}
      />

      {/* Home/Back button - top left corner */}
      <button
        className={styles.homeBackButton}
        onClick={() => { playUISound(); handleHome(); }}
        aria-label={t('pieceRules.backToHome')}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
      </button>

      {/* Left Panel */}
      <div className={styles.leftPanel}>
        <h1 className={styles.title}>{t('pieceRules.title')}</h1>

        <div className={styles.description}>
          <p>{t('pieceRules.description')}</p>
        </div>

        <div className={styles.pieceSection}>
          <h2 className={styles.sectionTitle}>{t('pieceRules.selectPiece')}</h2>
          <div className={styles.pieceDropdownContainer}>
            <select 
              className={styles.pieceDropdown}
              value={selectedPiece}
              onChange={handlePieceSelect}
            >
              {allPieces.map(piece => (
                <option key={piece} value={piece}>
                  {t(`pieces.${piece}`)}
                </option>
              ))}
            </select>
          </div>
        </div>


        <div className={styles.movementSection}>
          <h2 className={styles.sectionTitle}>{t('pieceRules.movement')}</h2>
          <div className={styles.movementButtons}>
            <button
              className={`${styles.abilityButton} ${playingScenario === 'movement' ? styles.active : ''}`}
              onClick={() => handleAbilitySelect('movement')}
            >
              {t('pieceRules.regularMovement')}
            </button>

            {/* Dynamic movement buttons for pieces with additional movement types */}
            {(() => {
              const database = getPieceAnimationDatabase();
              const pieceAnimations = database[selectedPiece as keyof typeof database];
              if (!pieceAnimations) return null;

              // Helper function to determine if a scenario should be in Movement section
              const isMovementScenario = (key: string) => {
                const animation = pieceAnimations[key as keyof typeof pieceAnimations];
                return animation?.scenarioType === 'movement';
              };

              const movementTypes = Object.keys(pieceAnimations).filter(key =>
                pieceAnimations[key as keyof typeof pieceAnimations]?.steps?.length > 0 &&
                key.toLowerCase() !== 'movement' &&
                isMovementScenario(key)
              );

              return movementTypes.map(movementType => {
                // Convert movement type to translation key (camelCase)
                const translationKey = movementType.toLowerCase().replace(/\s+/g, '');
                const translatedName = t(`moves.${translationKey}`) !== `moves.${translationKey}`
                  ? t(`moves.${translationKey}`)
                  : movementType.charAt(0).toUpperCase() + movementType.slice(1);

                return (
                  <button
                    key={movementType}
                    className={`${styles.abilityButton} ${playingScenario === movementType ? styles.active : ''}`}
                    onClick={() => handleAbilitySelect(movementType)}
                  >
                    {translatedName}
                  </button>
                );
              });
            })()}
          </div>
        </div>


        {/* Dynamic ability buttons for all pieces */}
        {(() => {
          const database = getPieceAnimationDatabase();
          const pieceAnimations = database[selectedPiece as keyof typeof database];
          if (!pieceAnimations) return null;

          // Helper function to determine if a scenario should be in Movement section
          const isMovementScenario = (key: string) => {
            const animation = pieceAnimations[key as keyof typeof pieceAnimations];
            return animation?.scenarioType === 'movement';
          };

          const abilities = Object.keys(pieceAnimations).filter(key =>
            pieceAnimations[key as keyof typeof pieceAnimations]?.steps?.length > 0 &&
            key.toLowerCase() !== 'movement' &&
            !isMovementScenario(key)
          );

          if (abilities.length === 0) return null;

          return (
            <div className={styles.abilitySection}>
              <h2 className={styles.sectionTitle}>{t('pieceRules.ability')}</h2>
              <div className={styles.abilityButtons}>
                {abilities.map(ability => {
                  // Convert ability name to translation key (camelCase)
                  const translationKey = ability.toLowerCase().replace(/\s+/g, '');
                  const translatedName = t(`moves.${translationKey}`) !== `moves.${translationKey}`
                    ? t(`moves.${translationKey}`)
                    : ability.charAt(0).toUpperCase() + ability.slice(1);

                  return (
                    <button
                      key={ability}
                      className={`${styles.abilityButton} ${playingScenario === ability ? styles.active : ''}`}
                      onClick={() => handleAbilitySelect(ability)}
                    >
                      {translatedName}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>

      {/* Game Area - hide on mobile selection view */}
      {!isMobile && (
        <div className={styles.gameArea}>
          <div className={styles.gameAreaContent}>
            <div className={styles.boardContainer}>
              <Board
                gameState={gameState}
                uiState={{
                  potentialMoves: potentialMoves,
                  potentialSideEffects: potentialSideEffects,
                }}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              />
            </div>

            {/* Right side panel with move context */}
            <div className={styles.rightPanel}>
              <MoveContext
                selectedPiece={selectedPiece}
                selectedAbility={selectedAbility}
                scenarioDescription={currentScenarioDescription}
                postMoveDescription={currentPostMoveDescription}
              />
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

export default PieceRules;
